import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Table, Button, Form, InputGroup, Nav, Modal, Spinner, Badge } from 'react-bootstrap';
import './PaymentMatching.css';
import api from '../services/api';
import { showToast } from '../components/ToastNotification';
import useColumnWidths from '../components/useColumnWidths';
import ColumnWidthsReset from '../components/ColumnWidthsReset';

/**
 * Fakturace → Párování plateb
 *
 * Ukazuje příchozí platby stažené z bankovních výpisů a jejich napojení na
 * faktury. Automat páruje podle variabilního symbolu (VS = číslo faktury);
 * co nevyjde, zůstane tady k ručnímu dopárování.
 *
 * Záložky:
 *   K dopárování — nespárované, částečně zatříděné a platby na fakturách,
 *                  které pořád nejsou celé uhrazené
 *   Vše          — všechny stažené platby
 *   Audit        — jak se která platba napárovala a kdo za tím stojí
 *   Synchronizace — historie běhů cronu i ručních spuštění
 */

const ITEMS_PER_PAGE = 50;

// ── Helpers ─────────────────────────────────────────────────────────────
const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value.substring(0, 10) + 'T00:00:00');
  if (isNaN(d.getTime())) return value;
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (isNaN(d.getTime())) return value;
  return `${formatDate(value)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const formatAmount = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  return num.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const STATUS_META = {
  unmatched: { label: 'Nespárovaná', cls: 'pm-badge-unmatched' },
  partial: { label: 'Částečně', cls: 'pm-badge-partial' },
  matched: { label: 'Spárovaná', cls: 'pm-badge-matched' },
  ignored: { label: 'Odložená', cls: 'pm-badge-ignored' },
};

/** Sloupce výpisu plateb — klíče, pod kterými se ukládají šířky. */
const SLOUPCE_PLATBY = ['datum', 'vs', 'platce', 'castka', 'nezatrideno', 'stav', 'faktura', 'akce'];

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || '—', cls: 'pm-badge-ignored' };
  return <span className={`pm-badge ${meta.cls}`}>{meta.label}</span>;
}

const TABY = [
  { id: 'attention', title: 'K dopárování', icon: 'bi-exclamation-triangle' },
  { id: 'all', title: 'Všechny platby', icon: 'bi-list-ul' },
  { id: 'audit', title: 'Audit spárování', icon: 'bi-clipboard-check' },
  { id: 'sync', title: 'Synchronizace', icon: 'bi-arrow-repeat' },
];

// ════════════════════════════════════════════════════════════════════════
function PaymentMatching({ openTab }) {
  const [aktivni, setAktivni] = useState('attention');

  // ── Seznam plateb ──────────────────────────────────────────────────
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Šířky sloupců tažením — ukládají se uživateli do DB (migrace 048).
  const { wrapRef, colgroup, tableStyle, resizerProps, maUlozene, vychozi } =
    useColumnWidths('payments.transactions', SLOUPCE_PLATBY);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // ── Audit + log ────────────────────────────────────────────────────
  const [audit, setAudit] = useState([]);
  const [auditSearch, setAuditSearch] = useState('');
  const [auditType, setAuditType] = useState('');
  const [syncLog, setSyncLog] = useState([]);

  // ── Synchronizace ──────────────────────────────────────────────────
  const [syncing, setSyncing] = useState(false);
  const [syncDays, setSyncDays] = useState(7);

  // ── Modal ručního párování ─────────────────────────────────────────
  const [matchModal, setMatchModal] = useState(null); // vybraná transakce
  const [candidates, setCandidates] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [includePaid, setIncludePaid] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [matchAmount, setMatchAmount] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Debounce hledání ───────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // ── Načtení plateb ─────────────────────────────────────────────────
  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: ITEMS_PER_PAGE };
      if (debouncedSearch) params.search = debouncedSearch;
      if (aktivni === 'attention') {
        params.status = 'attention';
      } else if (statusFilter) {
        params.status = statusFilter;
      }

      const res = await api.getBankPayments(params);
      setTransactions(res.data || []);
      setStats(res.stats || null);
      setTotalPages(res.pagination?.totalPages || 1);
      setTotalItems(res.pagination?.total || 0);
    } catch (e) {
      showToast('Nepodařilo se načíst platby: ' + e.message, 'danger');
    } finally {
      setLoading(false);
    }
  }, [aktivni, page, debouncedSearch, statusFilter]);

  const loadAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: ITEMS_PER_PAGE };
      if (auditSearch) params.search = auditSearch;
      if (auditType) params.matchType = auditType;
      const res = await api.getBankPaymentAudit(params);
      setAudit(res.data || []);
    } catch (e) {
      showToast('Nepodařilo se načíst audit: ' + e.message, 'danger');
    } finally {
      setLoading(false);
    }
  }, [auditSearch, auditType]);

  const loadSyncLog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getBankSyncLog(30);
      setSyncLog(res.data || []);
    } catch (e) {
      showToast('Nepodařilo se načíst historii: ' + e.message, 'danger');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (aktivni === 'attention' || aktivni === 'all') loadTransactions();
    else if (aktivni === 'audit') loadAudit();
    else if (aktivni === 'sync') loadSyncLog();
  }, [aktivni, loadTransactions, loadAudit, loadSyncLog]);

  // ── Spuštění „cronu" ───────────────────────────────────────────────
  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.syncBankPayments({ days: syncDays });
      const d = res.data || {};
      showToast(d.message || 'Synchronizace dokončena.', d.errors?.length ? 'warning' : 'success');
      if (d.errors?.length) {
        d.errors.forEach((err) => showToast(err, 'danger'));
      }
      if (aktivni === 'sync') loadSyncLog();
      else loadTransactions();
    } catch (e) {
      // Hlášky z backendu jsou samy o sobě srozumitelné, generický prefix je zbytečný.
      showToast(e.message, 'danger');
    } finally {
      setSyncing(false);
    }
  };

  // ── Ruční párování ─────────────────────────────────────────────────
  const openMatchModal = (transaction) => {
    setMatchModal(transaction);
    setSelectedInvoice(null);
    setMatchAmount('');
    setIncludePaid(false);
    // Předvyplníme VS — nejčastěji stačí a rovnou ukáže, na co platba míří.
    setCandidateSearch(transaction.variable_symbol || '');
    setCandidates([]);
  };

  const closeMatchModal = () => {
    setMatchModal(null);
    setCandidates([]);
    setSelectedInvoice(null);
  };

  const searchCandidates = useCallback(async (q, paid) => {
    if (!q || q.trim().length < 2) {
      setCandidates([]);
      return;
    }
    setCandidateLoading(true);
    try {
      const res = await api.searchBankPaymentInvoices(q.trim(), paid);
      setCandidates(res.data || []);
    } catch (e) {
      showToast('Hledání faktur selhalo: ' + e.message, 'danger');
    } finally {
      setCandidateLoading(false);
    }
  }, []);

  // Hledání kandidátů se pouští jen když je otevřený modal.
  useEffect(() => {
    if (!matchModal) return;
    const t = setTimeout(() => searchCandidates(candidateSearch, includePaid), 350);
    return () => clearTimeout(t);
  }, [matchModal, candidateSearch, includePaid, searchCandidates]);

  const pickInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    // Výchozí částka: kolik z platby zbývá, nejvýš dlužná částka faktury.
    const remaining = matchModal ? matchModal.remaining_amount : 0;
    setMatchAmount(String(Math.min(remaining, invoice.outstanding).toFixed(2)));
  };

  const handleMatch = async () => {
    if (!matchModal || !selectedInvoice) return;
    setSaving(true);
    try {
      const amount = matchAmount === '' ? null : parseFloat(matchAmount.replace(',', '.'));
      await api.matchBankPayment(matchModal.id, {
        invoiceId: selectedInvoice.id,
        amount,
      });
      showToast(`Platba napárovaná na fakturu ${selectedInvoice.number}.`, 'success');
      closeMatchModal();
      loadTransactions();
    } catch (e) {
      showToast(e.message, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleUnmatch = async (matchId) => {
    try {
      await api.unmatchBankPayment(matchId);
      showToast('Spárování zrušeno, úhrada odebrána z faktury.', 'success');
      if (aktivni === 'audit') loadAudit();
      else loadTransactions();
    } catch (e) {
      showToast(e.message, 'danger');
    }
  };

  const handleIgnore = async (transaction) => {
    try {
      if (transaction.status === 'ignored') {
        await api.unignoreBankPayment(transaction.id);
        showToast('Platba vrácena mezi nespárované.', 'success');
      } else {
        await api.ignoreBankPayment(transaction.id);
        showToast('Platba odložena stranou.', 'success');
      }
      loadTransactions();
    } catch (e) {
      showToast(e.message, 'danger');
    }
  };

  const openInvoice = (invoiceId, number) => {
    if (!openTab) return;
    openTab(`invoice-${invoiceId}`, `Faktura ${number || invoiceId}`, `/invoices/${invoiceId}`, 'InvoiceDetail', { invoiceId });
  };

  // ── Filtrovací pilulky ─────────────────────────────────────────────
  const pills = useMemo(() => ([
    { id: '', label: 'Vše', count: stats?.total },
    { id: 'unmatched', label: 'Nespárované', count: stats?.unmatched },
    { id: 'partial', label: 'Částečné', count: stats?.partial },
    { id: 'matched', label: 'Spárované', count: stats?.matched },
    { id: 'ignored', label: 'Odložené', count: stats?.ignored },
  ]), [stats]);

  // ══════════════════════════════════════════════════════════════════
  // Render — tabulka plateb
  // ══════════════════════════════════════════════════════════════════
  const renderTransactions = () => (
    <>
      {aktivni === 'all' && (
        <div className="pm-quick-filters">
          {pills.map((p) => (
            <button
              key={p.id || 'all'}
              type="button"
              className={`quick-filter-btn ${statusFilter === p.id ? 'active' : ''}`}
              onClick={() => { setStatusFilter(p.id); setPage(1); }}
            >
              {p.label}
              {p.count !== undefined && <span className="filter-count">{p.count}</span>}
            </button>
          ))}
        </div>
      )}

      {aktivni === 'attention' && stats?.unmatchedAmount > 0 && (
        <div className="pm-hint mb-3">
          <i className="bi bi-info-circle me-2"></i>
          Nezatříděno celkem <strong>{formatAmount(stats.unmatchedAmount)} Kč</strong>.
        </div>
      )}

      <div className="row g-3 align-items-end mb-3">
        <div className="col-12 col-md-4">
          <label className="form-label">Hledat VS, plátce, částku…</label>
          <InputGroup>
            <span className="input-group-text bg-white border-end-0">
              <i className="bi bi-search"></i>
            </span>
            <Form.Control
              className="border-start-0"
              placeholder="VS, název plátce, číslo účtu…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </InputGroup>
        </div>
        <div className="col-auto d-flex align-items-end">
          <ColumnWidthsReset maUlozene={maUlozene} vychozi={vychozi} />
        </div>
      </div>

      <div className="table-responsive grid-wrap" ref={wrapRef}>
        <Table hover className="align-middle pm-table grid-resizable" style={tableStyle}>
          {colgroup && (
            <colgroup>
              {colgroup.map(c => <col key={c.key} style={{ width: c.width }} />)}
            </colgroup>
          )}
          <thead>
            <tr>
              <th>Datum<span {...resizerProps('datum')} /></th>
              <th>VS<span {...resizerProps('vs')} /></th>
              <th>Plátce<span {...resizerProps('platce')} /></th>
              <th className="text-end">Částka<span {...resizerProps('castka')} /></th>
              <th className="text-end">Nezatříděno<span {...resizerProps('nezatrideno')} /></th>
              <th>Stav<span {...resizerProps('stav')} /></th>
              <th>Faktura / důvod<span {...resizerProps('faktura')} /></th>
              <th className="text-end no-print">Akce<span {...resizerProps('akce')} /></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center py-4"><Spinner size="sm" className="me-2" />Načítám…</td></tr>
            )}

            {!loading && transactions.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-5 text-muted">
                  <i className="bi bi-inbox d-block mb-2" style={{ fontSize: '2rem' }}></i>
                  {aktivni === 'attention'
                    ? 'Žádná platba nečeká na dopárování.'
                    : 'Zatím nejsou stažené žádné platby. Spusť synchronizaci nahoře.'}
                </td>
              </tr>
            )}

            {!loading && transactions.map((t) => {
              const unpaidMatch = t.matches.some((m) => m.invoice_paid === false);
              return (
                <tr key={t.id} className={unpaidMatch ? 'pm-row-attention' : ''}>
                  <td>{formatDate(t.transaction_date)}</td>
                  <td className="pm-mono">{t.variable_symbol || '—'}</td>
                  <td>
                    <div>{t.counterparty_name || '—'}</div>
                    {t.counterparty_account && (
                      <div className="pm-sub">{t.counterparty_account}{t.counterparty_bank_code ? `/${t.counterparty_bank_code}` : ''}</div>
                    )}
                  </td>
                  <td className="text-end pm-mono">{formatAmount(t.amount)} {t.currency || ''}</td>
                  <td className="text-end pm-mono">
                    {t.remaining_amount > 0
                      ? <strong className="text-danger">{formatAmount(t.remaining_amount)}</strong>
                      : <span className="text-muted">0,00</span>}
                  </td>
                  <td><StatusBadge status={t.status} /></td>
                  <td>
                    {t.matches.length > 0 ? (
                      <div className="pm-matches">
                        {t.matches.map((m) => (
                          <div key={m.id} className="pm-match">
                            <button
                              type="button"
                              className="pm-link"
                              onClick={() => openInvoice(m.invoice_id, m.invoice_number)}
                            >
                              {m.invoice_number}
                            </button>
                            <span className="pm-sub"> · {formatAmount(m.amount)} Kč</span>
                            {m.match_type === 'auto'
                              ? <Badge bg="light" text="dark" className="ms-1">auto</Badge>
                              : <Badge bg="light" text="dark" className="ms-1">ručně</Badge>}
                            {m.invoice_paid === false && (
                              <span className="pm-sub text-danger"> · faktura není celá uhrazená</span>
                            )}
                            <button
                              type="button"
                              className="pm-link pm-link-danger ms-2 no-print"
                              onClick={() => handleUnmatch(m.id)}
                              title="Zrušit spárování"
                            >
                              <i className="bi bi-x-circle"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="pm-sub">{t.match_note || '—'}</span>
                    )}
                  </td>
                  <td className="text-end no-print">
                    {t.remaining_amount > 0 && t.status !== 'ignored' && (
                      <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openMatchModal(t)}>
                        <i className="bi bi-link-45deg me-1"></i>Napárovat
                      </Button>
                    )}
                    <Button
                      variant={t.status === 'ignored' ? 'outline-secondary' : 'outline-secondary'}
                      size="sm"
                      onClick={() => handleIgnore(t)}
                      title={t.status === 'ignored' ? 'Vrátit mezi nespárované' : 'Odložit stranou'}
                    >
                      <i className={`bi ${t.status === 'ignored' ? 'bi-arrow-counterclockwise' : 'bi-archive'}`}></i>
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="small-muted">Celkem {totalItems} plateb</span>
          <div>
            <Button variant="outline-secondary" size="sm" className="me-2" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              <i className="bi bi-chevron-left"></i>
            </Button>
            <span className="small-muted mx-2">{page} / {totalPages}</span>
            <Button variant="outline-secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
              <i className="bi bi-chevron-right"></i>
            </Button>
          </div>
        </div>
      )}
    </>
  );

  // ══════════════════════════════════════════════════════════════════
  // Render — audit
  // ══════════════════════════════════════════════════════════════════
  const renderAudit = () => (
    <>
      <div className="row g-3 align-items-end mb-3">
        <div className="col-12 col-md-4">
          <label className="form-label">Hledat faktura, VS, plátce</label>
          <InputGroup>
            <span className="input-group-text bg-white border-end-0">
              <i className="bi bi-search"></i>
            </span>
            <Form.Control
              className="border-start-0"
              placeholder="číslo faktury, VS, plátce…"
              value={auditSearch}
              onChange={(e) => setAuditSearch(e.target.value)}
            />
          </InputGroup>
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label">Způsob</label>
          <Form.Select value={auditType} onChange={(e) => setAuditType(e.target.value)}>
            <option value="">Vše</option>
            <option value="auto">Automaticky</option>
            <option value="manual">Ručně</option>
          </Form.Select>
        </div>
      </div>

      <div className="table-responsive">
        <Table hover className="align-middle pm-table">
          <thead>
            <tr>
              <th style={{ width: 140 }}>Spárováno</th>
              <th style={{ width: 100 }}>Způsob</th>
              <th style={{ width: 110 }}>VS platby</th>
              <th>Plátce</th>
              <th>Faktura</th>
              <th className="text-end" style={{ width: 120 }}>Připsáno</th>
              <th className="text-end" style={{ width: 150 }}>Stav faktury</th>
              <th style={{ width: 140 }}>Kdo</th>
              <th className="text-end no-print" style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-4"><Spinner size="sm" className="me-2" />Načítám…</td></tr>}

            {!loading && audit.length === 0 && (
              <tr><td colSpan={9} className="text-center py-5 text-muted">Zatím není co auditovat.</td></tr>
            )}

            {!loading && audit.map((a) => (
              <tr key={a.id}>
                <td>{formatDateTime(a.created_at)}</td>
                <td>
                  {a.match_type === 'auto'
                    ? <span className="pm-badge pm-badge-auto">automaticky</span>
                    : <span className="pm-badge pm-badge-manual">ručně</span>}
                  {a.match_rule === 'variable_symbol' && <div className="pm-sub">podle VS</div>}
                </td>
                <td className="pm-mono">{a.transaction?.variable_symbol || '—'}</td>
                <td>
                  <div>{a.transaction?.counterparty_name || '—'}</div>
                  <div className="pm-sub">{formatDate(a.transaction?.date)} · {formatAmount(a.transaction?.amount)} {a.transaction?.currency}</div>
                </td>
                <td>
                  {a.invoice ? (
                    <button type="button" className="pm-link" onClick={() => openInvoice(a.invoice.id, a.invoice.number)}>
                      {a.invoice.number}
                    </button>
                  ) : '—'}
                  <div className="pm-sub">{a.invoice?.customer_name}</div>
                </td>
                <td className="text-end pm-mono">{formatAmount(a.amount)}</td>
                <td className="text-end">
                  {a.invoice && (
                    <>
                      <span className="pm-mono">{formatAmount(a.invoice.payed_amount)} / {formatAmount(a.invoice.total_price)}</span>
                      <div>
                        {a.invoice.paid
                          ? <span className="pm-badge pm-badge-matched">uhrazená</span>
                          : <span className="pm-badge pm-badge-partial">nedoplatek</span>}
                      </div>
                    </>
                  )}
                </td>
                <td>{a.matched_by || <span className="pm-sub">automat</span>}</td>
                <td className="text-end no-print">
                  <button
                    type="button"
                    className="pm-link pm-link-danger"
                    onClick={() => handleUnmatch(a.id)}
                    title="Zrušit spárování"
                  >
                    <i className="bi bi-x-circle"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </>
  );

  // ══════════════════════════════════════════════════════════════════
  // Render — historie synchronizací
  // ══════════════════════════════════════════════════════════════════
  const renderSyncLog = () => (
    <div className="table-responsive">
      <Table hover className="align-middle pm-table">
        <thead>
          <tr>
            <th style={{ width: 140 }}>Kdy</th>
            <th style={{ width: 110 }}>Banka</th>
            <th style={{ width: 170 }}>Období</th>
            <th style={{ width: 100 }}>Spuštění</th>
            <th className="text-end" style={{ width: 90 }}>Staženo</th>
            <th className="text-end" style={{ width: 90 }}>Nových</th>
            <th className="text-end" style={{ width: 90 }}>Duplicit</th>
            <th className="text-end" style={{ width: 90 }}>Výdajů</th>
            <th style={{ width: 100 }}>Stav</th>
            <th>Zpráva</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={10} className="text-center py-4"><Spinner size="sm" className="me-2" />Načítám…</td></tr>}

          {!loading && syncLog.length === 0 && (
            <tr><td colSpan={10} className="text-center py-5 text-muted">Synchronizace zatím neproběhla.</td></tr>
          )}

          {!loading && syncLog.map((s) => (
            <tr key={s.id}>
              <td>{formatDateTime(s.created_at)}</td>
              <td>{s.bank_code}{s.account_code ? ` / ${s.account_code}` : ''}</td>
              <td className="pm-sub">{formatDate(s.date_from)} – {formatDate(s.date_to)}</td>
              <td>{s.triggered_by === 'manual' ? 'ručně' : 'cron'}</td>
              <td className="text-end pm-mono">{s.fetched_count}</td>
              <td className="text-end pm-mono">{s.imported_count}</td>
              <td className="text-end pm-mono">{s.duplicate_count}</td>
              <td className="text-end pm-mono">{s.outgoing_count}</td>
              <td>
                {s.status === 'ok'
                  ? <span className="pm-badge pm-badge-matched">OK</span>
                  : <span className="pm-badge pm-badge-unmatched">chyba</span>}
              </td>
              <td className="pm-sub">{s.message || '—'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  const ZALOZKY = {
    attention: renderTransactions,
    all: renderTransactions,
    audit: renderAudit,
    sync: renderSyncLog,
  };

  // ══════════════════════════════════════════════════════════════════
  return (
    <Container fluid className="px-4 payment-matching">
      {/* Header */}
      <div className="header-bar mb-4">
        <Container fluid className="px-4">
          <div className="row align-items-center">
            <div className="col-md-5">
              <h4 className="mb-0">
                <i className="bi bi-bank me-2"></i>Párování plateb
              </h4>
            </div>
            <div className="col-md-7 text-end d-flex justify-content-end align-items-center gap-2 flex-wrap">
              <InputGroup size="sm" style={{ width: 170 }}>
                <InputGroup.Text>Dnů zpět</InputGroup.Text>
                <Form.Control
                  type="number"
                  min="1"
                  max="90"
                  value={syncDays}
                  onChange={(e) => setSyncDays(Math.max(1, Math.min(90, Number(e.target.value) || 1)))}
                />
              </InputGroup>
              <Button variant="primary" size="sm" onClick={handleSync} disabled={syncing}>
                {syncing
                  ? <><Spinner size="sm" className="me-1" />Stahuji…</>
                  : <><i className="bi bi-arrow-repeat me-1"></i>Stáhnout platby z banky</>}
              </Button>
            </div>
          </div>
        </Container>
      </div>

      {/* Záložky */}
      <div className="pm-tabs-wrapper">
        <Nav variant="tabs" activeKey={aktivni} onSelect={(k) => { setAktivni(k); setPage(1); }} className="pm-tabs">
          {TABY.map((t) => (
            <Nav.Item key={t.id}>
              <Nav.Link eventKey={t.id}>
                <i className={`bi ${t.icon} me-1`}></i>{t.title}
                {t.id === 'attention' && stats?.attention > 0 && (
                  <span className="pm-tab-count">{stats.attention}</span>
                )}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </div>
      <div className="pm-body">
        {(ZALOZKY[aktivni] || renderTransactions)()}
      </div>

      {/* Modal ručního párování */}
      <Modal show={!!matchModal} onHide={closeMatchModal} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Napárovat platbu na fakturu</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {matchModal && (
            <>
              <div className="pm-payment-summary mb-3">
                <div className="row g-2">
                  <div className="col-6 col-md-3">
                    <div className="pm-sub">Datum</div>
                    <div>{formatDate(matchModal.transaction_date)}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="pm-sub">Variabilní symbol</div>
                    <div className="pm-mono">{matchModal.variable_symbol || '—'}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="pm-sub">Částka</div>
                    <div className="pm-mono">{formatAmount(matchModal.amount)} {matchModal.currency}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="pm-sub">Zbývá zatřídit</div>
                    <div className="pm-mono fw-semibold">{formatAmount(matchModal.remaining_amount)} {matchModal.currency}</div>
                  </div>
                  <div className="col-12">
                    <div className="pm-sub">Plátce</div>
                    <div>{matchModal.counterparty_name || '—'}{matchModal.message ? ` · ${matchModal.message}` : ''}</div>
                  </div>
                </div>
              </div>

              <div className="row g-3 align-items-end mb-2">
                <div className="col-12 col-md-8">
                  <label className="form-label">Hledat fakturu podle čísla, zákazníka nebo čísla objednávky</label>
                  <InputGroup>
                    <span className="input-group-text bg-white border-end-0">
                      <i className="bi bi-search"></i>
                    </span>
                    <Form.Control
                      className="border-start-0"
                      placeholder="např. 212600023, Novák s.r.o., OBJ-2026-0145"
                      value={candidateSearch}
                      onChange={(e) => setCandidateSearch(e.target.value)}
                      autoFocus
                    />
                  </InputGroup>
                </div>
                <div className="col-12 col-md-4">
                  <Form.Check
                    type="checkbox"
                    id="pm-include-paid"
                    label="Zahrnout i uhrazené faktury"
                    checked={includePaid}
                    onChange={(e) => setIncludePaid(e.target.checked)}
                  />
                </div>
              </div>

              <div className="pm-candidates">
                {candidateLoading && <div className="text-center py-3"><Spinner size="sm" className="me-2" />Hledám…</div>}

                {!candidateLoading && candidates.length === 0 && candidateSearch.trim().length >= 2 && (
                  <div className="text-center py-3 text-muted">Nic nenalezeno.</div>
                )}

                {!candidateLoading && candidates.map((inv) => (
                  <button
                    key={inv.id}
                    type="button"
                    className={`pm-candidate ${selectedInvoice?.id === inv.id ? 'selected' : ''}`}
                    onClick={() => pickInvoice(inv)}
                  >
                    <div className="pm-candidate-main">
                      <strong>{inv.number}</strong>
                      <span className="pm-sub ms-2">{inv.customer_name}</span>
                      {inv.order_numbers?.length > 0 && (
                        <span className="pm-sub ms-2">· obj. {inv.order_numbers.join(', ')}</span>
                      )}
                    </div>
                    <div className="pm-candidate-amounts">
                      <span className="pm-sub">splatnost {formatDate(inv.due_date)}</span>
                      <span className="pm-mono ms-3">
                        {formatAmount(inv.payed_amount)} / {formatAmount(inv.total_price)} {inv.currency || 'CZK'}
                      </span>
                      <span className={`pm-mono ms-3 ${inv.outstanding > 0 ? 'text-danger fw-semibold' : 'text-success'}`}>
                        dluží {formatAmount(inv.outstanding)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {selectedInvoice && (
                <div className="row g-3 align-items-end mt-3">
                  <div className="col-12 col-md-5">
                    <label className="form-label">Připsat na fakturu {selectedInvoice.number}</label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={matchAmount}
                        onChange={(e) => setMatchAmount(e.target.value)}
                      />
                      <InputGroup.Text>{matchModal.currency || 'CZK'}</InputGroup.Text>
                    </InputGroup>
                  </div>
                  <div className="col-12 col-md-7">
                    <div className="pm-sub">
                      Když je částka nižší než dlužná, založí se částečná úhrada a faktura zůstane neuhrazená.
                      Zbytek platby zůstane volný pro další fakturu.
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeMatchModal} disabled={saving}>Zrušit</Button>
          <Button variant="primary" onClick={handleMatch} disabled={!selectedInvoice || saving}>
            {saving ? <><Spinner size="sm" className="me-1" />Ukládám…</> : <><i className="bi bi-link-45deg me-1"></i>Napárovat</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default PaymentMatching;
